import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '../../routes';
import { useAuth } from '../../context/AuthContext';
import { useCustomerSelector } from '../../context/CustomerSelectorContext';
import { CrudStyles } from '../shared/Crud';
import PageWithCustomerSelector from '../PageWithCustomerSelector';
import { useCrud } from '../../hooks/useCrud';
import deliveryService from '../../services/deliveryService';
import {
    deliveryProtocolReportClient,
    prepareShareableReportFile,
    presentReportFile,
    sharePreparedFile,
} from '../../services/reportService';
import { formatCurrencyBR } from '../../utils/masks';
import { getDefaultDeliveryDateRange, validateDeliveryDateRange } from '../../utils/deliveryDateRange';
import { saveDeliveryRequester } from '../../utils/deliveryRequesterStorage';
import DeliveryStatusCounts from './DeliveryStatusCounts';
import RequestersModal from './RequestersModal';
import DeliveryProtocolPrintModal from './DeliveryProtocolPrintModal';
import { PlusIcon, SearchIcon, EyeIcon, PrintIcon, XIcon } from '../Layout/icons';
import {
    DeliveryTable,
    ExpandedBlock,
    ActionCell,
    ActionBtn,
    ActionSeparator,
    MainRow,
    ExpandedRow,
    TabBar,
    TabButton,
    TabContent,
    FilterActions,
    DelayedFilter,
    FilterSwitch,
} from './DeliveriesList.styles';
import DeliveryStatusBadge from './DeliveryStatusBadge';
import DeliveryDelayedBadge from './DeliveryDelayedBadge';
import ResponsibleReceipt from '../ResponsibleReceipt';
import MessageModal from '../shared/MessageModal';

const { Container, Header, Button, FiltersContainer, FilterGroup, TableContainer, PaginationContainer, PageInfo, PageButtons, EmptyState } = CrudStyles;

const DeliveryIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <rect x="1" y="3" width="15" height="13" />
        <polygon points="16 8 20 8 23 11 23 16 16 16 16 8" />
        <circle cx="5.5" cy="18.5" r="2.5" />
        <circle cx="18.5" cy="18.5" r="2.5" />
    </svg>
);

function formatExpectedDate(value) {
    if (!value) return '—';
    try {
        const d = new Date(value);
        if (Number.isNaN(d.getTime())) return '—';
        return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
    } catch {
        return '—';
    }
}

function getDefaultDeliveryListFilters() {
    return {
        id: '',
        status: [],
        delayed: false,
        ...getDefaultDeliveryDateRange(),
    };
}

export default function Deliveries() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const { selectedCustomerId, isManagingOwnData, isTransportCompany } = useCustomerSelector();
    const prevCustomerRef = useRef(selectedCustomerId);
    const prevManagingRef = useRef(isManagingOwnData);
    const [showRequestersModal, setShowRequestersModal] = useState(false);
    const [creating, setCreating] = useState(false);
    const [protocolPrintTarget, setProtocolPrintTarget] = useState(null);
    const [protocolShareFile, setProtocolShareFile] = useState(null);
    const [printingProtocol, setPrintingProtocol] = useState(false);
    const [expandedId, setExpandedId] = useState(null);
    const [activeTabId, setActiveTabId] = useState('entregas');
    const [messageModal, setMessageModal] = useState({ open: false, title: 'Aviso', message: '' });

    const canPrintDeliveryProtocol = useMemo(() => {
        const roles = user?.roles || [];
        return roles.includes('ROLE_TRANSPORT_COMPANY') || roles.includes('ROLE_HR_EMPLOYEE');
    }, [user?.roles]);

    /** Aba "Responsáveis" só aparece para cliente ou empresa de transporte com cliente selecionado. */
    const canShowResponsiblesTab = isTransportCompany
        ? Boolean(selectedCustomerId && !isManagingOwnData)
        : Boolean(selectedCustomerId);

    const visibleTabs = useMemo(
        () =>
            [
                { id: 'entregas', label: 'Entregas', visible: true },
                { id: 'responsaveis', label: 'Responsáveis pelo recebimento', visible: canShowResponsiblesTab },
            ].filter((t) => t.visible),
        [canShowResponsiblesTab],
    );

    useEffect(() => {
        const isActiveVisible = visibleTabs.some((t) => t.id === activeTabId);
        if (!isActiveVisible && visibleTabs.length > 0) {
            setActiveTabId(visibleTabs[0].id);
        }
    }, [visibleTabs, activeTabId]);

    const [filterError, setFilterError] = useState(null);
    const [statusCounts, setStatusCounts] = useState([]);
    const [statusCountsLoading, setStatusCountsLoading] = useState(false);
    const [statusCountsError, setStatusCountsError] = useState(null);

    const adaptedService = useMemo(() => {
        const wrapList = (params, customerId) => {
            const validation = validateDeliveryDateRange(params.startDate, params.endDate);
            if (!validation.valid) {
                return Promise.resolve({
                    content: [],
                    page: { totalPages: 0, number: 0, first: true, last: true },
                });
            }
            return deliveryService.list({ ...params, customerId });
        };
        if (!isTransportCompany) {
            if (!selectedCustomerId) {
                return {
                    list: async () => ({ content: [], page: { totalPages: 0, number: 0, first: true, last: true } }),
                };
            }
            return {
                list: (params) => wrapList(params, selectedCustomerId),
            };
        }
        if (isManagingOwnData || !selectedCustomerId) {
            return {
                list: (params) => wrapList(params, undefined),
            };
        }
        return {
            list: (params) => wrapList(params, selectedCustomerId),
        };
    }, [isTransportCompany, selectedCustomerId, isManagingOwnData]);

    const {
        data,
        loading,
        pagination,
        handlePageChange,
        sorting,
        handleSort,
        localFilters,
        handleFilterChange,
        handleSearch,
        applyFilters,
        setLocalFilters,
        refetchTrigger,
        setRefetchTrigger,
        filters,
    } = useCrud(adaptedService, {
        pageSize: 20,
        defaultSort: 'id,desc',
        defaultFilters: getDefaultDeliveryListFilters(),
    });

    const handleDeliverySearch = () => {
        const validation = validateDeliveryDateRange(localFilters.startDate, localFilters.endDate);
        if (!validation.valid) {
            setFilterError(validation.message);
            return;
        }
        setFilterError(null);
        handleSearch();
    };

    const handleStatusToggle = (status) => {
        setFilterError(null);
        setLocalFilters((prev) => {
            const current = Array.isArray(prev.status) ? prev.status : [];
            const nextStatus = current.includes(status)
                ? current.filter((item) => item !== status)
                : [...current, status];
            return { ...prev, status: nextStatus };
        });
    };

    const handleClearFilters = () => {
        setFilterError(null);
        applyFilters(getDefaultDeliveryListFilters());
    };

    const handleDeliveryFilterChange = (e) => {
        setFilterError(null);
        handleFilterChange(e);
    };

    const handleDelayedFilterChange = () => {
        setFilterError(null);
        setLocalFilters((prev) => ({ ...prev, delayed: !prev.delayed }));
    };

    useEffect(() => {
        const validation = validateDeliveryDateRange(localFilters.startDate, localFilters.endDate);
        if (!validation.valid) {
            setFilterError(validation.message);
        }
    }, [localFilters.startDate, localFilters.endDate]);

    useEffect(() => {
        const customerChanged = prevCustomerRef.current !== selectedCustomerId;
        const managingChanged = prevManagingRef.current !== isManagingOwnData;
        prevCustomerRef.current = selectedCustomerId;
        prevManagingRef.current = isManagingOwnData;
        if (customerChanged || managingChanged) {
            setRefetchTrigger((prev) => prev + 1);
        }
    }, [selectedCustomerId, isManagingOwnData, setRefetchTrigger]);

    const statusCountCustomerId = useMemo(() => {
        if (!isTransportCompany) {
            return selectedCustomerId || undefined;
        }
        if (isManagingOwnData || !selectedCustomerId) {
            return undefined;
        }
        return selectedCustomerId;
    }, [isTransportCompany, selectedCustomerId, isManagingOwnData]);

    useEffect(() => {
        if (!selectedCustomerId) {
            setStatusCounts([]);
            setStatusCountsLoading(false);
            setStatusCountsError(null);
            return undefined;
        }

        const validation = validateDeliveryDateRange(filters.startDate, filters.endDate);
        if (!validation.valid) {
            return undefined;
        }

        let cancelled = false;
        const fetchCounts = async () => {
            setStatusCountsLoading(true);
            setStatusCountsError(null);
            try {
                const result = await deliveryService.listStatusCounts({
                    ...filters,
                    customerId: statusCountCustomerId,
                });
                if (!cancelled) {
                    setStatusCounts(result);
                }
            } catch (err) {
                if (!cancelled) {
                    setStatusCountsError(err?.message || 'Não foi possível carregar as contagens de status.');
                }
            } finally {
                if (!cancelled) {
                    setStatusCountsLoading(false);
                }
            }
        };
        fetchCounts();
        return () => {
            cancelled = true;
        };
    }, [
        filters,
        refetchTrigger,
        statusCountCustomerId,
        selectedCustomerId,
    ]);

    const getSortIcon = (field) => {
        if (!sorting || sorting.field !== field) return null;
        return sorting.direction === 'asc' ? ' ↑' : ' ↓';
    };

    const onSortClick = (field) => {
        if (!handleSort) return;
        const isAsc = sorting.field === field && sorting.direction === 'asc';
        handleSort(field, isAsc ? 'desc' : 'asc');
    };

    const getPersonId = () => user?.personId ?? localStorage.getItem('personId');

    const handleCreateDelivery = async (payload, requesterFromSelection = null) => {
        setCreating(true);
        try {
            const { deliveryId } = await deliveryService.createDelivery(payload);
            if (deliveryId) {
                saveDeliveryRequester(deliveryId, requesterFromSelection ?? {
                    id: payload.requesterId ?? null,
                    addressId: null,
                });
                navigate(ROUTES.ENTREGA_NOVA, { state: { deliveryId } });
            } else {
                setMessageModal({
                    open: true,
                    title: 'Aviso',
                    message: 'Não foi possível obter o ID da entrega criada.',
                });
            }
        } catch (err) {
            setMessageModal({
                open: true,
                title: 'Erro',
                message: err?.message || 'Erro ao criar entrega.',
            });
        } finally {
            setCreating(false);
        }
    };

    const handleNewDelivery = () => {
        if (isTransportCompany) {
            if (selectedCustomerId && !isManagingOwnData) {
                setShowRequestersModal(true);
            } else {
                navigate(ROUTES.ENTREGA_NOVA);
            }
            return;
        }
        const personId = getPersonId();
        if (!personId) {
            setMessageModal({
                open: true,
                title: 'Aviso',
                message: 'Dados do usuário incompletos. Faça login novamente.',
            });
            return;
        }
        handleCreateDelivery({ requesterId: personId });
    };

    const handleRequesterSelect = (employee) => {
        if (!selectedCustomerId) return;
        handleCreateDelivery(
            {
                customerId: selectedCustomerId,
                requesterId: employee.id,
            },
            { id: employee.id ?? null, addressId: employee.addressId ?? null }
        );
    };

    const handleDetails = (e, item) => {
        e.stopPropagation();
        const params = new URLSearchParams();
        if (item.customerId != null && String(item.customerId).trim() !== '') {
            params.set('customerId', String(item.customerId));
        }
        if (item.customerType != null && String(item.customerType).trim() !== '') {
            params.set('customerType', String(item.customerType));
        }
        const query = params.toString();
        const path = query ? `/entregas/${item.id}?${query}` : `/entregas/${item.id}`;
        navigate(path, {
            state: {
                deliveryCustomerId: item.customerId ?? null,
                deliveryCustomerType: item.customerType ?? null,
            },
        });
    };

    const handlePrint = (e, item) => {
        e.stopPropagation();
        if (!item?.id) return;

        const customerId =
            item.customerId != null && String(item.customerId).trim() !== ''
                ? item.customerId
                : !isManagingOwnData && selectedCustomerId != null && String(selectedCustomerId).trim() !== ''
                    ? selectedCustomerId
                    : null;

        if (customerId == null) {
            setMessageModal({
                open: true,
                title: 'Aviso',
                message: 'Não foi possível identificar o cliente da entrega para emitir o protocolo.',
            });
            return;
        }

        setProtocolShareFile(null);
        setProtocolPrintTarget({
            deliveryId: item.id,
            customerId,
        });
    };

    const closeProtocolPrint = () => {
        setProtocolPrintTarget(null);
        setProtocolShareFile(null);
    };

    const handleConfirmProtocolPrint = async (includeValue) => {
        if (!protocolPrintTarget?.deliveryId) return;

        setPrintingProtocol(true);
        try {
            const file = await deliveryProtocolReportClient.emit({
                deliveryId: Number(protocolPrintTarget.deliveryId),
                customerId: Number(protocolPrintTarget.customerId),
                includeValue: Boolean(includeValue),
            });
            const outcome = await presentReportFile(file);
            if (outcome === 'needs-gesture') {
                setProtocolShareFile(await prepareShareableReportFile(file));
                return;
            }
            closeProtocolPrint();
        } catch (err) {
            if (err?.name !== 'AbortError') {
                setMessageModal({
                    open: true,
                    title: 'Erro',
                    message: err?.message || 'Erro ao emitir protocolo de entrega.',
                });
            }
        } finally {
            setPrintingProtocol(false);
        }
    };

    const handleShareProtocol = () => {
        if (!protocolShareFile) return undefined;
        return sharePreparedFile(protocolShareFile)
            .then(() => closeProtocolPrint())
            .catch((err) => {
                if (err?.name === 'AbortError') return;
                setMessageModal({
                    open: true,
                    title: 'Erro',
                    message: 'Não foi possível abrir o compartilhamento. Tente novamente.',
                });
            });
    };

    const showEmptyState = isTransportCompany && !selectedCustomerId;

    if (showEmptyState) {
        return (
            <PageWithCustomerSelector
                selectorFirstOptionLabel="Todas as empresas"
                selectorFirstOptionDescription="Gerenciar todas as entregas em aberto"
                includePhysicalCustomers
            >
                {messageModal.open && (
                    <MessageModal
                        title={messageModal.title}
                        message={messageModal.message}
                        onClose={() => setMessageModal((m) => ({ ...m, open: false }))}
                    />
                )}
                <EmptyState>
                    <DeliveryIcon />
                    <h3>Selecione uma opção</h3>
                    <p>
                        Selecione &quot;Minha Empresa&quot; para gerenciar todas as entregas ou escolha um cliente no seletor acima para ver apenas as entregas daquele cliente.
                    </p>
                </EmptyState>
            </PageWithCustomerSelector>
        );
    }

    return (
        <PageWithCustomerSelector
            selectorFirstOptionLabel="Todas as empresas"
            selectorFirstOptionDescription="Gerenciar todas as entregas em aberto"
            includePhysicalCustomers
        >
            {messageModal.open && (
                <MessageModal
                    title={messageModal.title}
                    message={messageModal.message}
                    onClose={() => setMessageModal((m) => ({ ...m, open: false }))}
                />
            )}
            {showRequestersModal && selectedCustomerId && (
                <RequestersModal
                    customerId={selectedCustomerId}
                    onSelect={handleRequesterSelect}
                    onSkip={() => handleCreateDelivery({ customerId: selectedCustomerId })}
                    onClose={() => setShowRequestersModal(false)}
                />
            )}
            <DeliveryProtocolPrintModal
                open={Boolean(protocolPrintTarget)}
                isSubmitting={printingProtocol}
                shareFile={protocolShareFile}
                onClose={() => !printingProtocol && closeProtocolPrint()}
                onConfirm={handleConfirmProtocolPrint}
                onShare={handleShareProtocol}
            />
            {visibleTabs.length > 1 && (
                <TabBar>
                    {visibleTabs.map((tab) => (
                        <TabButton
                            key={tab.id}
                            type="button"
                            $active={activeTabId === tab.id}
                            onClick={() => setActiveTabId(tab.id)}
                        >
                            {tab.label}
                        </TabButton>
                    ))}
                </TabBar>
            )}
            <TabContent>
                {activeTabId === 'entregas' && (
            <Container>
                <Header>
                    <h1>Entregas</h1>
                    {!(isTransportCompany && isManagingOwnData) && (
                        <Button variant="primary" onClick={handleNewDelivery} disabled={creating}>
                            <PlusIcon />
                            Nova entrega
                        </Button>
                    )}
                </Header>

                <DeliveryStatusCounts
                    items={statusCounts}
                    selectedStatuses={Array.isArray(localFilters.status) ? localFilters.status : []}
                    onToggle={handleStatusToggle}
                    loading={statusCountsLoading}
                    error={statusCountsError}
                />

                <FiltersContainer>
                    <FilterGroup>
                        <label htmlFor="delivery-filter-id">Entrega</label>
                        <input
                            id="delivery-filter-id"
                            type="number"
                            name="id"
                            min="1"
                            placeholder="Nº da entrega"
                            value={localFilters.id}
                            onChange={handleDeliveryFilterChange}
                        />
                    </FilterGroup>
                    <FilterGroup>
                        <label htmlFor="delivery-filter-start">Criação de</label>
                        <input
                            id="delivery-filter-start"
                            type="date"
                            name="startDate"
                            value={localFilters.startDate}
                            onChange={handleDeliveryFilterChange}
                        />
                    </FilterGroup>
                    <FilterGroup>
                        <label htmlFor="delivery-filter-end">Criação até</label>
                        <input
                            id="delivery-filter-end"
                            type="date"
                            name="endDate"
                            value={localFilters.endDate}
                            onChange={handleDeliveryFilterChange}
                        />
                    </FilterGroup>
                    <DelayedFilter>
                        <span id="delivery-filter-delayed-label">Atrasadas</span>
                        <FilterSwitch
                            id="delivery-filter-delayed"
                            type="button"
                            role="switch"
                            aria-labelledby="delivery-filter-delayed-label"
                            aria-checked={localFilters.delayed === true}
                            $on={localFilters.delayed === true}
                            onClick={handleDelayedFilterChange}
                        />
                    </DelayedFilter>
                    <FilterActions>
                        <Button type="button" variant="primary" onClick={handleDeliverySearch} style={{ height: '38px' }}>
                            <SearchIcon />
                            Pesquisar
                        </Button>
                        <Button type="button" variant="outline" onClick={handleClearFilters} style={{ height: '38px' }}>
                            <XIcon />
                            Limpar filtros
                        </Button>
                    </FilterActions>
                </FiltersContainer>
                {filterError ? (
                    <p style={{ margin: '-8px 0 0', color: '#b91c1c', fontSize: 13 }}>{filterError}</p>
                ) : (
                    <p style={{ margin: '-8px 0 0', color: '#6b7280', fontSize: 12 }}>
                        Filtro por data de criação da entrega (intervalo máximo de 60 dias).
                    </p>
                )}

                <TableContainer>
                    <DeliveryTable>
                        <thead>
                            <tr>
                                <th onClick={() => onSortClick('id')} style={{ cursor: 'pointer' }}>Entrega{getSortIcon('id')}</th>
                                {isTransportCompany && <th>Cliente</th>}
                                <th onClick={() => onSortClick('scheduledDeliveryDateTime')} style={{ cursor: 'pointer' }}>
                                    Data agendada para a entrega{getSortIcon('scheduledDeliveryDateTime')}
                                </th>
                                <th onClick={() => onSortClick('state')} style={{ cursor: 'pointer' }}>Status{getSortIcon('state')}</th>
                                <th onClick={() => onSortClick('value')} style={{ cursor: 'pointer' }}>
                                    Valor{getSortIcon('value')}
                                </th>
                                <th>Ações</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan={isTransportCompany ? 6 : 5} style={{ textAlign: 'center', padding: '32px' }}>
                                        Carregando...
                                    </td>
                                </tr>
                            ) : data.length === 0 ? (
                                <tr>
                                    <td colSpan={isTransportCompany ? 6 : 5} style={{ textAlign: 'center', padding: '32px' }}>
                                        Nenhum registro encontrado
                                    </td>
                                </tr>
                            ) : (
                                data.map((item) => (
                                    <React.Fragment key={item.id}>
                                        <MainRow
                                            $unviewed={!item.viewed}
                                            onClick={() => setExpandedId((prev) => (prev === item.id ? null : item.id))}
                                        >
                                            <td>#{item.id}</td>
                                            {isTransportCompany && <td>{item.customerName || '—'}</td>}
                                            <td>{formatExpectedDate(item.expectedDate)}</td>
                                            <td>
                                                <DeliveryStatusBadge state={item.state || item.status} />
                                            </td>
                                            <td>{item.value != null ? formatCurrencyBR(item.value) : '—'}</td>
                                            <ActionCell onClick={(e) => e.stopPropagation()}>
                                                {item.delayed ? (
                                                    <>
                                                        <DeliveryDelayedBadge delayed />
                                                        <ActionSeparator>|</ActionSeparator>
                                                    </>
                                                ) : null}
                                                <ActionBtn type="button" onClick={(e) => handleDetails(e, item)} title="Detalhes">
                                                    <EyeIcon />
                                                    Detalhes
                                                </ActionBtn>
                                                {canPrintDeliveryProtocol ? (
                                                    <>
                                                        <ActionSeparator>|</ActionSeparator>
                                                        <ActionBtn
                                                            type="button"
                                                            onClick={(e) => handlePrint(e, item)}
                                                            title="Impressão"
                                                            disabled={printingProtocol}
                                                        >
                                                            <PrintIcon />
                                                            Impressão
                                                        </ActionBtn>
                                                    </>
                                                ) : null}
                                            </ActionCell>
                                        </MainRow>
                                        {expandedId === item.id && (
                                            <ExpandedRow>
                                                <td colSpan={isTransportCompany ? 6 : 5}>
                                                    <ExpandedBlock>
                                                        <div><strong>Endereço de coleta:</strong> {item.pickupAddress || '—'}</div>
                                                        <div><strong>Endereço de entrega:</strong> {item.deliveryAddress || '—'}</div>
                                                        <div><strong>Destinatário:</strong> {item.recipientName || '—'}</div>
                                                    </ExpandedBlock>
                                                </td>
                                            </ExpandedRow>
                                        )}
                                    </React.Fragment>
                                ))
                            )}
                        </tbody>
                    </DeliveryTable>
                </TableContainer>

                {pagination && (
                    <PaginationContainer>
                        <PageInfo>
                            Página {pagination.totalPages === 0 ? 0 : pagination.page + 1} de {pagination.totalPages || 0}
                        </PageInfo>
                        <PageButtons>
                            <Button
                                variant="outline"
                                disabled={pagination.totalPages === 0 || pagination.first}
                                onClick={() => handlePageChange(pagination.page - 1)}
                            >
                                Anterior
                            </Button>
                            <Button
                                variant="outline"
                                disabled={pagination.totalPages === 0 || pagination.last}
                                onClick={() => handlePageChange(pagination.page + 1)}
                            >
                                Próxima
                                </Button>
                            </PageButtons>
                        </PaginationContainer>
                )}
            </Container>
                )}
                {activeTabId === 'responsaveis' && <ResponsibleReceipt embedded />}
            </TabContent>
        </PageWithCustomerSelector>
    );
}
